import type { ReactNode } from "react";
import { CalendarDots, CreditCard, PencilSimple, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/format";
import { summarizeDays } from "../onboarding-logic";
import type { DayAvailability, ServiceFormState } from "../types";

const MODALITY_LABEL: Record<ServiceFormState["modality"], string> = {
  online: "online",
  in_person: "uživo",
  both: "online i uživo",
};

interface Props {
  days: DayAvailability[];
  service: ServiceFormState;
  /** Bruto cijena koju klijent plaća; null dok se konfiguracija cijena ne učita. */
  clientPrice: number | null;
  /** Ostale aktivne usluge osim prikazane; uređuju se u Postavkama, ne ovdje. */
  otherServiceCount: number;
  editingAvailability: boolean;
  onToggleAvailability: () => void;
  availabilityForm: ReactNode;
  editingService: boolean;
  onToggleService: () => void;
  serviceForm: ReactNode;
}

/**
 * Pregled podataka prenesenih sa stare platforme (migrirani terapeut). Umjesto da
 * prolazi kroz dvije pune forme, terapeut vidi sažetak i otvara formu samo za ono
 * što treba ispraviti.
 */
export function ReviewStep({
  days,
  service,
  clientPrice,
  otherServiceCount,
  editingAvailability,
  onToggleAvailability,
  availabilityForm,
  editingService,
  onToggleService,
  serviceForm,
}: Props) {
  const schedule = summarizeDays(days);

  return (
    <div className="space-y-4">
      <ReviewCard
        icon={<CalendarDots className="size-5" weight="duotone" />}
        title="Radno vrijeme"
        editing={editingAvailability}
        onToggle={onToggleAvailability}
        form={availabilityForm}
      >
        {schedule.length > 0 ? (
          <ul className="space-y-0.5">
            {schedule.map((line) => <li key={line}>{line}</li>)}
          </ul>
        ) : (
          <p>Nije postavljeno.</p>
        )}
      </ReviewCard>

      <ReviewCard
        icon={<CreditCard className="size-5" weight="duotone" />}
        title="Usluga"
        editing={editingService}
        onToggle={onToggleService}
        form={serviceForm}
      >
        <p className="font-medium text-foreground">{service.name}</p>
        <p>
          {service.durationMinutes} min, {MODALITY_LABEL[service.modality]}
          {clientPrice !== null && <>, klijent plaća <b className="tabular-nums text-foreground">{formatCurrency(clientPrice)}</b></>}
          {clientPrice !== null && service.price !== null && (
            <span className="text-xs"> ({formatCurrency(service.price)} bez PDV-a)</span>
          )}
        </p>
        {otherServiceCount > 0 && (
          <p className="mt-1 text-xs">
            Ostale usluge ({otherServiceCount}) možeš urediti kasnije u meniju Usluge.
          </p>
        )}
      </ReviewCard>

      <p className="text-sm text-muted-foreground">
        Ako je sve tačno, samo klikni dalje. Sve možeš promijeniti i kasnije u Postavkama.
      </p>
    </div>
  );
}

function ReviewCard({
  icon,
  title,
  editing,
  onToggle,
  form,
  children,
}: {
  icon: ReactNode;
  title: string;
  editing: boolean;
  onToggle: () => void;
  form: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-start gap-3 p-4">
        <span className="text-primary">{icon}</span>
        <div className="min-w-0 flex-1 text-sm text-muted-foreground">
          <h4 className="mb-1 text-sm font-semibold text-foreground">{title}</h4>
          {!editing && children}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onToggle}>
          {editing ? <X className="size-4" /> : <PencilSimple className="size-4" />}
          {editing ? "Odustani" : "Izmijeni"}
        </Button>
      </div>
      {editing && <div className="border-t border-border p-4">{form}</div>}
    </div>
  );
}
